import React, {useEffect} from 'react';
import {ScrollView, View} from 'react-native';
import Toast from 'react-native-toast-message';
import {ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import AccessYourCard from '../../../components/rewards/AccessYourCard';
import AddToWallet from '../../../components/rewards/AddToWallet';
import InStoreCode from '../../../components/rewards/InStoreCode';
import MemberCard from '../../../components/rewards/MemberCard';
import OptOutCTA from '../../../components/rewards/OptOutCTA';
import {RewardsCardSkeleton, SkeletonGroup} from '../../../components/skeleton';
import Text from '../../../components/themed/Text';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import logoImg from '../../../images/brand/header-logo.png';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useRewardsAccount} from '../../../queries/rewards/useRewardsAccount';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';
import {resolveMemberDisplayName} from '../../../utils/rewards';
import RewardsListScreen from '../../articles/RewardsListScreen';

const AccountRewardsScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.REWARDS | AppRoute.ACCOUNT_REWARDS>) => {
    const {rewardsStore, userStore} = useStores();

    // DIS-1094: Fetch rewards data via TanStack Query for logged-in users.
    // Reports to Sentry on first failure, retries 2x with exponential backoff.
    // DIS-1175 follow-up: /account-details is the authoritative source for the
    // member card's staff / lmg-staff badge and card fields. Enable it for ANY
    // logged-in user with a phone — not only when hasRewards is false — so a
    // member viewing their card self-heals stale persisted flags (the badge was
    // rendering whatever won the boot race, with no render-time refresh).
    const isLoggedInWithPhone = userStore.isLoggedIn && !!userStore.phoneNumber;
    const isLoggedInWithoutRewards = isLoggedInWithPhone && !rewardsStore.hasRewards;

    const {data, isLoading, isError} = useRewardsAccount(
      isLoggedInWithPhone ? userStore.phoneNumber : undefined,
    );

    // DIS-1321: Sync TanStack Query result into MobX via the canonical
    // applyAccountDetails merge point (Zen-authoritative WIRE_WINS /
    // WIRE_TRISTATE). Gate on data.exists so the empty-shape pre-fetch
    // does not clobber persisted state.
    useEffect(() => {
      if (data?.exists) {
        rewardsStore.applyAccountDetails(data);
      }
    }, [data, rewardsStore]);

    // Error: show toast only when there is no card to fall back to (non-member
    // first load). A member's card renders from cached state, so a transient
    // /account-details failure — now that the query runs for members too — must
    // stay silent instead of alarming over a working card.
    useEffect(() => {
      if (isError && !rewardsStore.hasRewards) {
        Toast.show({
          type: ERROR_MESSAGE,
          text1: "Couldn't load your rewards card right now.",
        });
      }
    }, [isError, rewardsStore]);

    // Loading state: logged in, fetching rewards
    if (isLoggedInWithoutRewards && isLoading) {
      return (
        <ThemeProvider theme={rewardsTheme}>
          <ScrollView>
            <ContentView>
              <SkeletonGroup isLoading={true} skeleton={<RewardsCardSkeleton />}>
                <View />
              </SkeletonGroup>
            </ContentView>
          </ScrollView>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={rewardsTheme}>
        {rewardsStore.hasRewards ? (
          <AccountRewardsHasRewardsScreen route={route} navigation={navigation} />
        ) : (
          <RewardsListScreen navigation={navigation} route={route} applyHeaderInset={false} />
        )}
      </ThemeProvider>
    );
  },
);

const AccountRewardsHasRewardsScreen = observer(
  ({
    navigation: _navigation,
    route: _route,
  }: RouteNavigatorScreenProps<AppRoute.REWARDS | AppRoute.ACCOUNT_REWARDS>) => {
    const {rewardsStore, userStore} = useStores();
    // DIS-1245: lite users have no MFL session, so userStore stays empty
    // forever. Prefer rewardsStore (populated by lite signUp/signUpStaff)
    // and fall back to userStore for full-session paths.
    const memberName = resolveMemberDisplayName(
      {firstName: rewardsStore.firstName, lastName: rewardsStore.lastName},
      {firstName: userStore.firstName, lastName: userStore.lastName},
      'AccountRewardsScreen',
    );
    const greetFirstName = rewardsStore.firstName || userStore.firstName || '';
    return (
      <ScrollView>
        <ContentView testID="account-rewards-has-rewards-screen">
          <Text variant="heading1">Your rewards card</Text>
          <Text variant="body2">
            {greetFirstName ? `Congrats, ${greetFirstName}!` : 'Congrats!'} You are now a part of
            our rewards program! Show this barcode at checkout to collect awesome rewards!
          </Text>
          <View style={{gap: 8, marginTop: 24}}>
            <MemberCard
              logoImg={logoImg}
              cardNumber={rewardsStore.cardNumber}
              name={memberName}
              memberSince={rewardsStore.memberSince}
              isStaff={rewardsStore.isStaffSignup}
              isLmgStaff={rewardsStore.isLmgStaff}
            />
            {/* DIS-1556: Zen only issues the consumer card template, so the wallet
                pass never generates for staff members. Hide the dead button. */}
            {!rewardsStore.isStaffSignup && !rewardsStore.isLmgStaff && (
              <AddToWallet walletUrl={rewardsStore.walletUrl} />
            )}
          </View>
          <Text style={{fontWeight: 600, marginTop: 50}} variant="heading2">
            Your temporary rewards code
          </Text>
          <Text variant="body2" style={{marginTop: 8}}>
            Want to keep ahold of your phone? Give this code at checkout instead.
          </Text>
          <InStoreCode inStoreCode={rewardsStore.shortcode} />
          <View style={{height: 50}} />
          <AccessYourCard />
          <View style={{height: 50}} />
          <OptOutCTA
            onOptOut={() => rewardsStore.optOut(userStore.phoneNumber, userStore.lastName)}
          />
          <View style={{height: 50}} />
        </ContentView>
      </ScrollView>
    );
  },
);

export default AccountRewardsScreen;
